import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  EntryLabelPrintControl,
  type LabelPrintShow,
} from '@/components/entries/EntryLabelPrintControl'
import { useLabelPrinter } from '@/context/label-printer'
import { useEntrantPacket } from '@/api/hooks'
import { openPdfWindow } from '@/lib/pdf-window'
import type { TEntrySummary } from '@schemas'
import { useCheckInStepperContext } from '../CheckInStepper'
import { PrinterSetupBanner } from './PrinterSetupBanner'

type FinalizeStepProps = {
  labelPrintShow?: LabelPrintShow
  heading?: string
  description?: string
  showId: string | null
  showName: string | null
  isOnline: boolean
  packetPermissionDisabled: boolean
  packetPermissionReason?: string
  printerSetupMessage?: string
  labelPrintingDisabled?: boolean
}

function formatContact(details: Array<string | null | undefined>) {
  return details.filter(value => typeof value === 'string' && value.trim().length > 0).join(' • ')
}

function formatRoleLabel(role: string | null | undefined) {
  if (!role) return null
  return role
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function FinalizeStep({
  labelPrintShow,
  heading = 'Finalize entries',
  description,
  showId,
  showName,
  isOnline,
  packetPermissionDisabled,
  packetPermissionReason,
  printerSetupMessage,
  labelPrintingDisabled = false,
}: FinalizeStepProps) {
  const stepper = useCheckInStepperContext()
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const { selectedEntrant } = stepper

  const entrantPacket = useEntrantPacket()
  const { strategy: labelStrategy, strategyLabel, selectedPrinter } = useLabelPrinter()
  const [printing, setPrinting] = useState(false)

  useEffect(() => {
    headingRef.current?.focus()
  }, [selectedEntrant?.id])

  const entries = useMemo(() => {
    if (!selectedEntrant?.entries) return []
    return (selectedEntrant.entries.filter(Boolean) as TEntrySummary[]).sort((a, b) => {
      const aCode = a.entryCode ?? ''
      const bCode = b.entryCode ?? ''
      return aCode.localeCompare(bCode)
    })
  }, [selectedEntrant?.entries])

  const handleBackToEntries = useCallback(() => {
    stepper.goToStep('entries')
  }, [stepper])

  const handleDone = useCallback(() => {
    stepper.setSelectedEntrant(null)
    stepper.goToStep('entrant')
  }, [stepper])

  const handlePrintPacket = useCallback(async () => {
    if (!selectedEntrant?.id) {
      toast.error('Select an entrant before printing their packet.')
      return
    }
    if (!showId) {
      toast.error('Select a show before printing entrant packets.')
      return
    }
    if (packetPermissionDisabled) {
      toast.error(
        packetPermissionReason ?? 'Printing entrant packets requires additional permissions.'
      )
      return
    }
    if (!isOnline) {
      toast.error('Connect to the network before printing entrant packets.')
      return
    }

    let packetWindow: ReturnType<typeof openPdfWindow> | undefined
    try {
      setPrinting(true)
      const showLabel = showName?.trim() || 'Show'
      const entrantLabel = selectedEntrant.name ?? selectedEntrant.person?.name ?? 'Entrant'
      packetWindow = openPdfWindow(
        `${showLabel} – ${entrantLabel} packet`,
        labelStrategy === 'browser'
      )
      const result = await entrantPacket.mutateAsync({
        showId,
        exhibitorId: selectedEntrant.id,
      })

      packetWindow.show(result.buffer)

      const entryCount =
        typeof result.entryCount === 'number' && Number.isFinite(result.entryCount)
          ? result.entryCount
          : entries.length
      const suffix =
        entryCount > 0 ? ` (${entryCount} ${entryCount === 1 ? 'entry' : 'entries'})` : ''
      toast.success(`Entrant packet ready${suffix}`)
    } catch (error) {
      packetWindow?.close()
      const message = error instanceof Error ? error.message : 'Unable to print entrant packet'
      toast.error(message)
    } finally {
      setPrinting(false)
    }
  }, [
    selectedEntrant?.id,
    selectedEntrant?.name,
    selectedEntrant?.person?.name,
    showId,
    showName,
    packetPermissionDisabled,
    packetPermissionReason,
    isOnline,
    entrantPacket,
    labelStrategy,
    entries.length,
  ])

  const printPending = entrantPacket.isPending || printing
  let printDisabledReason: string | undefined
  if (!selectedEntrant?.id) {
    printDisabledReason = 'Select an entrant before printing.'
  } else if (!showId) {
    printDisabledReason = 'Select a show before printing.'
  } else if (packetPermissionDisabled) {
    printDisabledReason =
      packetPermissionReason ?? 'Printing entrant packets requires additional permissions.'
  } else if (!isOnline) {
    printDisabledReason = 'Connect to the network to print entrant packets.'
  }

  const printDisabled = Boolean(printDisabledReason) || printPending
  const printerConfigured = labelStrategy === 'browser' || Boolean(selectedPrinter)
  const showPrinterSetupBanner = !labelPrintingDisabled && !printerConfigured
  const printerBannerMessage =
    printerSetupMessage ??
    (labelStrategy === 'dymo'
      ? 'Select a DYMO printer to auto-print Exhibit IDs after check-in.'
      : 'Set up label printing to auto-print Exhibit IDs after check-in.')

  const finalizationStatus = useMemo(() => {
    if (!selectedEntrant) {
      return 'Select an entrant to finalize entries.'
    }
    if (!selectedEntrant.finalizedAt) {
      return 'Entries not finalized yet.'
    }
    const timestamp = new Date(selectedEntrant.finalizedAt)
    const formatted = Number.isNaN(timestamp.getTime())
      ? selectedEntrant.finalizedAt
      : timestamp.toLocaleString()
    const roleLabel = formatRoleLabel(selectedEntrant.finalizedByRole ?? null)
    return roleLabel ? `Finalized ${formatted} by ${roleLabel}` : `Finalized ${formatted}`
  }, [selectedEntrant])

  return (
    <div className="space-y-4" data-testid="finalize-step-content">
      <Card>
        <CardHeader>
          <CardTitle ref={headingRef} tabIndex={-1}>
            {heading}
          </CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent className="space-y-6">
          {showPrinterSetupBanner ? (
            <PrinterSetupBanner
              message={printerBannerMessage}
              className="border-muted/60 bg-muted/30"
            />
          ) : null}
          {selectedEntrant ? (
            <Fragment>
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-foreground">
                  {selectedEntrant.name ?? selectedEntrant.person?.name ?? 'Entrant'}
                </span>
                <span className="text-muted-foreground">
                  {formatContact([
                    selectedEntrant.email,
                    selectedEntrant.phone,
                    selectedEntrant.person?.email,
                  ]) || 'No contact details provided'}
                </span>
                {selectedEntrant.entrantCode ? (
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    Entrant code <Badge variant="secondary">{selectedEntrant.entrantCode}</Badge>
                  </span>
                ) : null}
                {selectedEntrant.person?.name &&
                selectedEntrant.person?.name !== selectedEntrant.name ? (
                  <span className="text-xs text-muted-foreground">
                    Linked person: {selectedEntrant.person.name}
                  </span>
                ) : null}
                <span className="text-xs text-muted-foreground" data-testid="finalization-status">
                  {finalizationStatus}
                </span>
              </div>

              <div className="rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground">
                <p>
                  Printer strategy: <span className="font-medium">{strategyLabel}</span>
                </p>
                <p>
                  Entrant packets open in a new tab. Use the browser print dialog to produce packet
                  copies.
                </p>
                {labelStrategy === 'dymo' ? (
                  <p>
                    DYMO printers will print via the browser dialog. Ensure the DYMO LabelWriter is
                    selected before confirming.
                  </p>
                ) : null}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Entries
                </h3>
                {entries.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Exhibit ID</TableHead>
                        <TableHead>Class</TableHead>
                        {labelPrintShow && <TableHead>Labels</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <span className="font-mono text-sm">
                              {entry.entryCode ?? 'Pending'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col text-xs">
                              <span className="font-medium text-foreground">
                                {entry.class?.name ?? 'Class pending'}
                              </span>
                              {entry.class?.code ? (
                                <span className="text-muted-foreground">{entry.class.code}</span>
                              ) : null}
                            </div>
                          </TableCell>
                          {labelPrintShow && (
                            <TableCell>
                              <EntryLabelPrintControl entry={entry} show={labelPrintShow} />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No entries are associated with this entrant yet.
                  </p>
                )}
              </div>
            </Fragment>
          ) : (
            <p className="text-sm text-muted-foreground">
              Select an entrant and check in their entries to prepare packets.
            </p>
          )}

          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleBackToEntries}>
                Back to Entries
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handlePrintPacket}
                disabled={printDisabled}
                title={printDisabledReason}
              >
                {printPending ? 'Preparing…' : 'Print Packet'}
              </Button>
              <Button type="button" onClick={handleDone} variant="default">
                Done
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
